package diana.soleil.design.iraniancanadienbazzar.core.data.firestore

import diana.soleil.design.iraniancanadienbazzar.core.data.UserPreferences
import diana.soleil.design.iraniancanadienbazzar.core.data.UserProfile
import diana.soleil.design.iraniancanadienbazzar.core.model.Address
import diana.soleil.design.iraniancanadienbazzar.core.model.AppNotification
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessClaim
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessSource
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimReviewStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimRole
import diana.soleil.design.iraniancanadienbazzar.core.model.Category
import diana.soleil.design.iraniancanadienbazzar.core.model.Comment
import diana.soleil.design.iraniancanadienbazzar.core.model.DailyPlatformCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Conversation
import diana.soleil.design.iraniancanadienbazzar.core.model.DailyListingCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.EventCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.EventStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.EventVenue
import diana.soleil.design.iraniancanadienbazzar.core.model.Message
import diana.soleil.design.iraniancanadienbazzar.core.model.NotificationType
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.UserAccount
import diana.soleil.design.iraniancanadienbazzar.core.model.MediaStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.ItemCondition
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.ItemStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceItem
import diana.soleil.design.iraniancanadienbazzar.core.model.Metros
import diana.soleil.design.iraniancanadienbazzar.core.model.NewsArticle
import diana.soleil.design.iraniancanadienbazzar.core.model.Order
import diana.soleil.design.iraniancanadienbazzar.core.model.OrderItem
import diana.soleil.design.iraniancanadienbazzar.core.model.OrderStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.PaymentStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Product
import diana.soleil.design.iraniancanadienbazzar.core.model.PropertyDetails
import diana.soleil.design.iraniancanadienbazzar.core.model.Ticket
import diana.soleil.design.iraniancanadienbazzar.core.model.TicketStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.TicketType
import diana.soleil.design.iraniancanadienbazzar.core.model.Post
import diana.soleil.design.iraniancanadienbazzar.core.model.PostType
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingRequest
import diana.soleil.design.iraniancanadienbazzar.core.model.ModerationAction
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingRequestStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingRequestType
import diana.soleil.design.iraniancanadienbazzar.core.model.Report
import diana.soleil.design.iraniancanadienbazzar.core.model.ReportStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Review
import diana.soleil.design.iraniancanadienbazzar.core.model.Sponsorship
import diana.soleil.design.iraniancanadienbazzar.core.model.SponsorshipScope
import dev.gitlive.firebase.firestore.BaseTimestamp
import dev.gitlive.firebase.firestore.Timestamp
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * Firestore-wire DTOs decoded by GitLive (`documentSnapshot.data<T>()`). Kept separate from the domain
 * models because (a) GitLive uses its own decoder, not kotlinx-JSON, so the domain `LocalizedText`'s
 * custom JSON serializer can't be used here, and (b) the wire shape carries optional/server fields the
 * domain omits. Localized fields are the **object form** `{en,fr,fa}` ([LocalizedTextDoc]); the app
 * writes that form. Bare-string legacy/seed values aren't decodable into a structure — such documents
 * are skipped by the source (see `FirestoreRepositories`) until re-seeded in object form
 * (see `docs/LOCALIZATION.md`).
 */
@Serializable
internal data class LocalizedTextDoc(
    val en: String? = null,
    val fr: String? = null,
    val fa: String? = null,
) {
    fun toModel(): LocalizedText = LocalizedText(en = en, fr = fr, fa = fa)
}

@Serializable
internal data class AddressDoc(
    val line: String = "",
    val city: String = "",
    val province: String = "",
    val postalCode: String? = null,
    val country: String = "CA",
    val lat: Double? = null,
    val lng: Double? = null,
) {
    fun toModel(): Address = Address(line, city, province, postalCode, country, lat, lng)
}

@Serializable
internal data class SponsorshipDoc(
    val active: Boolean = false,
    val plan: String = "free",
    val scope: SponsorshipScope? = null,
    val startsAt: String? = null,
    val endsAt: String? = null,
) {
    fun toModel(): Sponsorship = Sponsorship(active, plan, scope, startsAt, endsAt)
}

/** Result of the `createPromotionCheckout` callable — the Stripe hosted-Checkout URL to open. */
@Serializable
internal data class PromotionCheckoutResult(val url: String? = null)

@Serializable
internal data class SourceDoc(
    val url: String? = null,
    val directoryUrl: String? = null,
    val retrievedAt: String? = null,
    val verificationStatus: String? = null,
    val reviewRequired: Boolean = true,
) {
    fun toModel(): BusinessSource = BusinessSource(url, directoryUrl, retrievedAt, verificationStatus, reviewRequired)
}

@Serializable
internal data class BusinessDoc(
    val name: String = "",
    val nameLocalized: LocalizedTextDoc? = null,
    val serviceTitle: LocalizedTextDoc? = null,
    val ownerUid: String? = null,
    val claimStatus: ClaimStatus = ClaimStatus.Unclaimed,
    val claimRequestedBy: String? = null,
    val categoryId: String = "",
    val description: LocalizedTextDoc? = null,
    val status: BusinessStatus = BusinessStatus.Pending,
    val verified: Boolean = false,
    val phone: String? = null,
    val email: String? = null,
    val website: String? = null,
    val address: AddressDoc = AddressDoc(),
    val metroId: String? = null,
    val hours: Map<String, String> = emptyMap(),
    val logoUrl: String? = null,
    val coverUrl: String? = null,
    val photos: List<String> = emptyList(),
    val mediaStatus: MediaStatus = MediaStatus.CategoryPlaceholderPending,
    val languagesSpoken: List<String> = emptyList(),
    val attributes: List<String> = emptyList(),
    val offersChat: Boolean = false,
    val offersOrdering: Boolean = false,
    val priceLevel: Int? = null,
    val rating: Double = 0.0,
    val reviewCount: Int = 0,
    val endorsementCount: Int = 0,
    val sponsorship: SponsorshipDoc = SponsorshipDoc(),
    val ownerPro: Boolean = false,
    val source: SourceDoc? = null,
    val editedSinceApproval: Boolean = false,
) {
    fun toModel(id: String): Business = Business(
        id = id,
        name = name,
        nameLocalized = nameLocalized?.toModel(),
        serviceTitle = serviceTitle?.toModel(),
        ownerUid = ownerUid,
        claimStatus = claimStatus,
        claimRequestedBy = claimRequestedBy,
        categoryId = categoryId,
        description = description?.toModel() ?: LocalizedText(),
        status = status,
        verified = verified,
        phone = phone,
        email = email,
        website = website,
        address = address.toModel(),
        metroId = metroId ?: Metros.forCity(address.city)?.id,
        hours = hours,
        logoUrl = logoUrl,
        coverUrl = coverUrl,
        photos = photos,
        mediaStatus = mediaStatus,
        languagesSpoken = languagesSpoken,
        attributes = attributes,
        offersChat = offersChat,
        offersOrdering = offersOrdering,
        priceLevel = priceLevel,
        rating = rating,
        reviewCount = reviewCount,
        endorsementCount = endorsementCount,
        sponsorship = sponsorship.toModel(),
        ownerPro = ownerPro,
        editedSinceApproval = editedSinceApproval,
        source = source?.toModel(),
    )
}

/**
 * First-write shape of an owner-submitted listing from the Add Business wizard. Rules pin
 * `ownerUid == uid()`, `status == 'pending'`, `verified == false`, and `keys().hasOnly(...)`;
 * server-managed aggregates (rating/reviewCount/endorsementCount/sponsorship) are deliberately
 * OMITTED — the rules' "absent-or-safe" pins accept that and the decode defaults fill them in.
 */
@Serializable
internal data class BusinessCreateDoc(
    val name: String,
    val nameLocalized: LocalizedTextDoc,
    val ownerUid: String,
    val claimStatus: ClaimStatus,
    val categoryId: String,
    val description: LocalizedTextDoc,
    val status: String,
    val verified: Boolean,
    val address: AddressDoc,
    val metroId: String?,
    val attributes: List<String>,
    val mediaStatus: MediaStatus,
    val createdAt: BaseTimestamp,
)

/**
 * First-write shape of a business ownership claim (`claims/{claimId}`). Rules force
 * `claimantUid == uid()` and `status == 'pending'`; `reviewedAt`/`reviewedBy` are set later by the
 * admin approve/reject path (so they're absent on create). `createdAt` is server-resolved.
 */
@Serializable
internal data class ClaimCreateDoc(
    val businessId: String,
    val businessName: String,
    val claimantUid: String,
    val claimantName: String,
    val role: ClaimRole,
    val contactEmail: String,
    val contactPhone: String,
    val message: String,
    val documentRef: String?,
    val status: ClaimReviewStatus,
    val createdAt: BaseTimestamp,
)

/** Read shape for `claims/{claimId}` (admin review queue). Tolerant — every field has a default. */
@Serializable
internal data class ClaimDoc(
    val businessId: String = "",
    val businessName: String = "",
    val claimantUid: String = "",
    val claimantName: String = "",
    val role: ClaimRole = ClaimRole.Owner,
    val contactEmail: String = "",
    val contactPhone: String = "",
    val message: String = "",
    val documentRef: String? = null,
    val status: ClaimReviewStatus = ClaimReviewStatus.Pending,
    val createdAt: Timestamp? = null,
    val reviewedAt: Timestamp? = null,
    val reviewedBy: String? = null,
) {
    fun toModel(id: String): BusinessClaim = BusinessClaim(
        id = id,
        businessId = businessId,
        businessName = businessName,
        claimantUid = claimantUid,
        claimantName = claimantName,
        role = role,
        contactEmail = contactEmail,
        contactPhone = contactPhone,
        message = message,
        documentRef = documentRef,
        status = status,
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
        reviewedAtMillis = reviewedAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
        reviewedBy = reviewedBy,
    )
}

/**
 * First-write shape of `users/{uid}` (rules force `role == 'user'` on create). No property defaults:
 * every field must reach the wire regardless of the encoder's encodeDefaults setting.
 */
@Serializable
internal data class UserCreateDoc(
    val role: String,
    val displayName: String?,
    val preferredLanguage: String,
    val metro: String?,
    val savedBusinessIds: List<String>,
    val savedNewsIds: List<String>,
    val interests: List<String>,
    val createdAt: BaseTimestamp,
)

/** Merge-write shape for preference updates on an existing `users/{uid}` (role untouched). */
@Serializable
internal data class UserPrefsDoc(
    val preferredLanguage: String,
    val metro: String?,
    val savedBusinessIds: List<String>,
    val savedNewsIds: List<String>,
    val interests: List<String>,
)

/** Read shape for `reviews/{authorUid}_{businessId}` (tolerant — legacy docs may lack fields). */
@Serializable
internal data class ReviewReadDoc(
    val businessId: String = "",
    val authorUid: String = "",
    val authorName: String = "",
    val rating: Int = 0,
    val comment: String = "",
    val tags: List<String> = emptyList(),
    val ownerReply: String? = null,
    val status: String = "visible",
    val createdAt: Timestamp? = null,
) {
    fun toModel(id: String): Review = Review(
        id = id,
        businessId = businessId,
        authorUid = authorUid,
        authorName = authorName,
        rating = rating,
        comment = comment,
        tags = tags,
        ownerReply = ownerReply,
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
    )
}

/**
 * First-write shape of a review. `ownerReply`/`helpfulCount` are written explicitly (null/0) because
 * the author-edit rule checks `unchanged(...)` on them — a rules read of a *missing* key errors and
 * denies, so they must exist from day one.
 */
@Serializable
internal data class ReviewCreateDoc(
    val businessId: String,
    val authorUid: String,
    val authorName: String,
    val rating: Int,
    val comment: String,
    val tags: List<String>,
    val ownerReply: String?,
    val helpfulCount: Int,
    val status: String,
    val createdAt: BaseTimestamp,
)

/** Merge-write shape for a business owner's reply (the ONLY review field owners may write). */
@Serializable
internal data class ReviewOwnerReplyDoc(
    val ownerReply: String?,
)

/** Merge-write shape for an author editing their existing review (server fields untouched). */
@Serializable
internal data class ReviewEditDoc(
    val rating: Int,
    val comment: String,
    val tags: List<String>,
)

/** Read shape for a follow relationship (`follows/{uid}_{businessId}`). */
@Serializable
internal data class FollowReadDoc(
    val userId: String = "",
    val businessId: String = "",
)

/** Create shape when a user follows a business. */
@Serializable
internal data class FollowCreateDoc(
    val userId: String,
    val businessId: String,
    val createdAt: BaseTimestamp,
)

@Serializable
internal data class EventVenueDoc(
    val name: String = "",
    val address: String = "",
    val city: String = "",
    val province: String = "",
) {
    fun toModel(): EventVenue = EventVenue(name, address, city, province)
}

/** Firestore shape for discovery-only event listings. */
@Serializable
internal data class EventDoc(
    val organizerUid: String = "",
    val businessId: String? = null,
    val title: LocalizedTextDoc = LocalizedTextDoc(),
    val description: LocalizedTextDoc = LocalizedTextDoc(),
    val coverUrl: String? = null,
    val startsAt: Timestamp? = null,
    val endsAt: Timestamp? = null,
    val venue: EventVenueDoc = EventVenueDoc(),
    val category: String = "culture",
    val metroId: String? = null,
    val ticketUrl: String? = null,
    val isFree: Boolean = false,
    val featured: Boolean = false,
    val status: String = "pending",
    val createdAt: Timestamp? = null,
) {
    fun toModel(id: String): Event? {
        val start = startsAt ?: return null
        return Event(
            id = id,
            organizerUid = organizerUid,
            title = title.toModel(),
            description = description.toModel(),
            coverUrl = coverUrl,
            startsAtMillis = start.seconds * 1_000 + start.nanoseconds / 1_000_000,
            endsAtMillis = endsAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
            venue = venue.toModel(),
            category = when (category) {
                "music" -> EventCategory.Music
                "family" -> EventCategory.Family
                "business" -> EventCategory.Business
                "other" -> EventCategory.Other
                else -> EventCategory.Culture
            },
            metroId = metroId,
            ticketUrl = ticketUrl,
            isFree = isFree,
            featured = featured,
            status = when (status) {
                "approved" -> EventStatus.Approved
                "rejected" -> EventStatus.Rejected
                "cancelled" -> EventStatus.Cancelled
                else -> EventStatus.Pending
            },
        )
    }
}

/** Wire shape of a ticket type (`events/{id}/ticketTypes/{tid}`). */
@Serializable
internal data class TicketTypeDoc(
    val name: String = "",
    val priceCents: Long = 0,
    val quantityTotal: Int? = null,
    val quantitySold: Int = 0,
) {
    fun toModel(id: String): TicketType = TicketType(
        id = id,
        name = name,
        priceCents = priceCents,
        quantityTotal = quantityTotal,
        quantitySold = quantitySold,
    )
}

/** Read shape of an issued ticket (`tickets/{id}` — server-created; event identity snapshotted). */
@Serializable
internal data class TicketDoc(
    val eventId: String = "",
    val ticketTypeId: String = "",
    val buyerUid: String = "",
    val qty: Int = 1,
    val qrPayload: String = "",
    val status: String = "valid",
    val eventTitle: LocalizedTextDoc? = null,
    val startsAt: Timestamp? = null,
    val venueName: String = "",
    val purchasedAt: Timestamp? = null,
) {
    fun toModel(id: String): Ticket = Ticket(
        id = id,
        eventId = eventId,
        ticketTypeId = ticketTypeId,
        qty = qty,
        qrPayload = qrPayload,
        status = when (status) {
            "used" -> TicketStatus.Used
            "refunded" -> TicketStatus.Refunded
            else -> TicketStatus.Valid
        },
        eventTitle = eventTitle?.toModel() ?: LocalizedText(),
        startsAtMillis = startsAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
        venueName = venueName,
        purchasedAtMillis = purchasedAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
    )
}

/** Wire shape of a product (`businesses/{id}/products/{pid}`). */
@Serializable
internal data class ProductDoc(
    val name: LocalizedTextDoc? = null,
    val description: LocalizedTextDoc? = null,
    val priceCents: Long = 0,
    val imageUrl: String? = null,
    val available: Boolean = true,
    val promoted: Boolean = false,
    val property: PropertyDetailsDoc? = null,
) {
    fun toModel(id: String, businessId: String): Product = Product(
        id = id,
        businessId = businessId,
        name = name?.toModel() ?: LocalizedText(),
        description = description?.toModel() ?: LocalizedText(),
        priceCents = priceCents,
        imageUrl = imageUrl,
        available = available,
        promoted = promoted,
        property = property?.toModel(),
    )
}

/** Wire shape of the optional real-estate facts map on a product. */
@Serializable
internal data class PropertyDetailsDoc(
    val bedrooms: Int? = null,
    val bathrooms: Double? = null,
    val areaSqFt: Int? = null,
    val address: String? = null,
) {
    fun toModel(): PropertyDetails = PropertyDetails(
        bedrooms = bedrooms,
        bathrooms = bathrooms,
        areaSqFt = areaSqFt,
        address = address,
    )
}

internal fun PropertyDetails.toDoc(): PropertyDetailsDoc = PropertyDetailsDoc(
    bedrooms = bedrooms,
    bathrooms = bathrooms,
    areaSqFt = areaSqFt,
    address = address,
)

/** Write shape of a product (owner upsert). */
@Serializable
internal data class ProductWriteDoc(
    val name: LocalizedTextDoc,
    val description: LocalizedTextDoc,
    val priceCents: Long,
    val available: Boolean,
    val promoted: Boolean,
    val property: PropertyDetailsDoc? = null,
)

/** Wire shape of one order line. */
@Serializable
internal data class OrderItemDoc(
    val productId: String = "",
    val name: String = "",
    val qty: Int = 0,
    val priceCents: Long = 0,
) {
    fun toModel(): OrderItem = OrderItem(productId = productId, name = name, quantity = qty, priceCents = priceCents)
}

/** Read shape of an order. */
@Serializable
internal data class OrderDoc(
    val buyerUid: String = "",
    val businessId: String = "",
    val businessName: String = "",
    val items: List<OrderItemDoc> = emptyList(),
    val subtotalCents: Long = 0,
    val totalCents: Long = 0,
    val status: String = "pending",
    val paymentStatus: String = "unpaid",
    val note: String = "",
    val createdAt: Timestamp? = null,
) {
    fun toModel(id: String): Order = Order(
        id = id,
        businessId = businessId,
        businessName = businessName,
        items = items.map { it.toModel() },
        subtotalCents = subtotalCents,
        totalCents = totalCents,
        status = when (status) {
            "confirmed" -> OrderStatus.Confirmed
            "fulfilled" -> OrderStatus.Fulfilled
            "cancelled" -> OrderStatus.Cancelled
            else -> OrderStatus.Pending
        },
        paymentStatus = when (paymentStatus) {
            "paid" -> PaymentStatus.Paid
            "refunded" -> PaymentStatus.Refunded
            else -> PaymentStatus.Unpaid
        },
        note = note,
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
    )
}

/** First-write shape of an order (rules force buyer + pending + unpaid). */
@Serializable
internal data class OrderCreateDoc(
    val buyerUid: String,
    val businessId: String,
    val businessName: String,
    val items: List<OrderItemDoc>,
    val subtotalCents: Long,
    val totalCents: Long,
    val status: String,
    val paymentStatus: String,
    val note: String,
    val createdAt: BaseTimestamp,
)

/** Wire shape of a marketplace listing. */
@Serializable
internal data class MarketplaceItemDoc(
    val sellerUid: String = "",
    val sellerName: String = "",
    val title: String = "",
    val description: String = "",
    val priceCents: Long? = null,
    val isFree: Boolean = false,
    val condition: String = "used",
    val category: String = "other",
    val metroId: String? = null,
    val city: String = "",
    val photoUrls: List<String> = emptyList(),
    val status: String = "active",
    val createdAt: Timestamp? = null,
    val featured: Boolean = false,
) {
    fun toModel(id: String): MarketplaceItem = MarketplaceItem(
        id = id,
        sellerUid = sellerUid,
        sellerName = sellerName,
        title = title,
        description = description,
        priceCents = priceCents,
        isFree = isFree,
        condition = when (condition) {
            "new" -> ItemCondition.New
            "like_new" -> ItemCondition.LikeNew
            else -> ItemCondition.Used
        },
        category = MarketplaceCategory.fromWire(category),
        metroId = metroId,
        city = city,
        photoUrls = photoUrls,
        status = when (status) {
            "sold" -> ItemStatus.Sold
            "removed" -> ItemStatus.Removed
            else -> ItemStatus.Active
        },
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
        featured = featured,
    )
}

/** First-write shape of a listing. */
@Serializable
internal data class MarketplaceItemCreateDoc(
    val sellerUid: String,
    val sellerName: String,
    val title: String,
    val description: String,
    val priceCents: Long?,
    val isFree: Boolean,
    val condition: String,
    val category: String,
    val metroId: String?,
    val city: String,
    val photoUrls: List<String>,
    val status: String,
    val createdAt: BaseTimestamp,
)

/** Merge-write shape for a seller editing their listing (identity/status/photos untouched). */
@Serializable
internal data class MarketplaceItemUpdateDoc(
    val title: String,
    val description: String,
    val priceCents: Long?,
    val isFree: Boolean,
    val condition: String,
    val category: String,
    val city: String,
)

/** Wire shape of a feed post. */
@Serializable
internal data class PostDoc(
    val authorUid: String = "",
    val authorName: String = "",
    val type: String = "general",
    val text: String = "",
    val metroId: String? = null,
    val status: String = "visible",
    val commentCount: Int = 0,
    val likeCount: Int = 0,
    val createdAt: Timestamp? = null,
    val photoUrl: String? = null,
) {
    fun toModel(id: String): Post = Post(
        id = id,
        authorUid = authorUid,
        authorName = authorName,
        type = when (type) {
            "question" -> PostType.Question
            "recommendation" -> PostType.Recommendation
            else -> PostType.General
        },
        text = text,
        metroId = metroId,
        commentCount = commentCount,
        likeCount = likeCount,
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
        photoUrl = photoUrl,
    )
}

/** Wire shape of a block-list entry (`users/{uid}/blockedUsers/{targetUid}`). */
@Serializable
internal data class BlockedUserDoc(
    val name: String = "",
    val createdAt: BaseTimestamp? = null,
)

/** Wire shape of a post like (`posts/{postId}/likes/{uid}` — doc id is the liker's uid). */
@Serializable
internal data class PostLikeDoc(
    val uid: String,
    val postId: String,
    val createdAt: BaseTimestamp,
)

/** Wire shape of a private bookmark (`users/{uid}/savedPosts/{postId}`). */
@Serializable
internal data class SavedPostDoc(
    val createdAt: BaseTimestamp,
)

/** First-write shape of a post (commentCount written explicitly — rules lock it on author edits). */
@Serializable
internal data class PostCreateDoc(
    val authorUid: String,
    val authorName: String,
    val type: String,
    val text: String,
    val metroId: String?,
    val status: String,
    val commentCount: Int,
    val createdAt: BaseTimestamp,
)

/** Read shape for a post comment (`posts/{postId}/comments/{id}`). */
@Serializable
internal data class CommentDoc(
    val authorUid: String = "",
    val authorName: String = "",
    val text: String = "",
    val recommendedBusinessId: String? = null,
    val recommendedBusinessName: String? = null,
    val createdAt: Timestamp? = null,
) {
    fun toModel(id: String, currentUid: String?): Comment = Comment(
        id = id,
        authorUid = authorUid,
        authorName = authorName,
        text = text,
        recommendedBusinessId = recommendedBusinessId,
        recommendedBusinessName = recommendedBusinessName,
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
        isMine = currentUid != null && currentUid == authorUid,
    )
}

/** First-write shape of a comment. */
@Serializable
internal data class CommentCreateDoc(
    val authorUid: String,
    val authorName: String,
    val text: String,
    val recommendedBusinessId: String? = null,
    val recommendedBusinessName: String? = null,
    val createdAt: BaseTimestamp,
)

/**
 * Create shape for `recommendations/{uid}_{businessId}` — one lightweight endorsement per user per
 * business, minted when a comment recommends a business ("Ask the Mahaleh"). The id convention makes
 * duplicates structurally impossible (same trick as reviews); a Cloud Function folds the count into
 * `businesses/{id}.endorsementCount`.
 */
@Serializable
internal data class RecommendationCreateDoc(
    val uid: String,
    val businessId: String,
    val postId: String,
    val createdAt: BaseTimestamp,
)

/** Read shape for `conversations/{id}` (business / item / dm threads). */
@Serializable
internal data class ConversationDoc(
    val businessId: String = "",
    val businessName: String = "",
    val itemId: String? = null,
    val participantUids: List<String> = emptyList(),
    val lastMessage: String = "",
    val lastMessageAt: Timestamp? = null,
    val type: String = Conversation.TYPE_BUSINESS,
    val acceptedUids: List<String> = emptyList(),
    val createdBy: String? = null,
    /** DM display names snapshot ({uid: name}) — the inbox renders the counterparty's name from it. */
    val participantNames: Map<String, String> = emptyMap(),
) {
    fun toModel(id: String, selfUid: String? = null): Conversation {
        // A DM's display title is the OTHER participant's snapshotted name; business/item keep theirs.
        val display = if (type == Conversation.TYPE_DM) {
            val other = participantUids.firstOrNull { it != selfUid }
            // `?:` alone was not enough: threads created before 2026-09-17 snapshotted an EMPTY
            // name for members who signed up with email/password, and an empty string is not null,
            // so the inbox rendered a blank row. Blank is missing — the UI labels it.
            participantNames[other]?.takeIf { it.isNotBlank() } ?: businessName
        } else {
            businessName
        }
        return Conversation(
            id = id,
            businessId = businessId,
            businessName = display,
            itemId = itemId,
            participantUids = participantUids,
            lastMessage = lastMessage,
            lastMessageAtMillis = lastMessageAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
            type = type,
            acceptedUids = acceptedUids,
            participantNames = participantNames,
            createdBy = createdBy,
        )
    }
}

/** First-write shape of a conversation (created on first contact). */
@Serializable
internal data class ConversationCreateDoc(
    val businessId: String,
    val businessName: String,
    val itemId: String?,
    val participantUids: List<String>,
    val lastMessage: String,
    val lastMessageAt: BaseTimestamp,
    val type: String = Conversation.TYPE_BUSINESS,
    val acceptedUids: List<String> = emptyList(),
    val createdBy: String? = null,
    val participantNames: Map<String, String> = emptyMap(),
)

/** Read shape for `publicProfiles/{uid}` (user discovery + DM display). */
@Serializable
internal data class PublicProfileDoc(
    val displayName: String = "",
    val displayNameLower: String = "",
    val city: String? = null,
    val bio: String? = null,
    val intents: List<String> = emptyList(),
    val discoverable: Boolean = false,
    val mapVisible: Boolean = false,
    val approxLat: Double? = null,
    val approxLng: Double? = null,
    val mapPrecision: String? = null,
    val mapCheckInExpiresAt: Long? = null,
    val photoUrl: String? = null,
    val photos: List<String> = emptyList(),
    val occupation: String? = null,
    val hometown: String? = null,
    val relationship: String? = null,
    val languages: List<String> = emptyList(),
    val interests: List<String> = emptyList(),
) {
    fun toModel(uid: String): diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile =
        diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile(
            uid = uid, displayName = displayName, city = city,
            bio = bio, intents = intents, discoverable = discoverable,
            mapVisible = mapVisible, approxLat = approxLat, approxLng = approxLng,
            mapPrecision = diana.soleil.design.iraniancanadienbazzar.core.model.MapPrecision.fromWire(mapPrecision, mapVisible),
            mapCheckInExpiresAtMillis = mapCheckInExpiresAt,
            photoUrl = photoUrl, photos = photos, occupation = occupation, hometown = hometown,
            relationship = relationship, languages = languages, interests = interests,
        )
}

/** Merge-write shape of a people-map check-in / check-out (approximate coords snapped by the repo). */
@Serializable
internal data class MapVisibilityWriteDoc(
    val mapVisible: Boolean,
    val approxLat: Double?,
    val approxLng: Double?,
    /** `city` (~11 km grid) | `area` (~2 km grid); null when hidden. */
    val mapPrecision: String?,
    /** Epoch millis the check-in ends (rules cap it at ~4 h out); null when hidden. */
    val mapCheckInExpiresAt: Long?,
)

/** Merge-write shape of the Find-your-people card (`publicProfiles/{uid}` discovery fields). */
@Serializable
internal data class DiscoveryProfileWriteDoc(
    val bio: String?,
    val intents: List<String>,
    val discoverable: Boolean,
)

/** Merge-write shape of the public self-disclosure fields (person-profile editor). */
@Serializable
internal data class SocialProfileWriteDoc(
    val city: String?,
    val occupation: String?,
    val hometown: String?,
    val relationship: String?,
    val languages: List<String>,
    val interests: List<String>,
)

/** Merge-write shape stamping a freshly uploaded profile photo. */
@Serializable
internal data class ProfilePhotoWriteDoc(
    val photoUrl: String,
)

/** Merge-write shape for the public photo gallery (`publicProfiles/{uid}.photos`). */
@Serializable
internal data class ProfilePhotosWriteDoc(
    val photos: List<String>,
)

/** First-write/merge shape of `publicProfiles/{uid}` (client-mirrored from the user's own profile). */
@Serializable
internal data class PublicProfileWriteDoc(
    val displayName: String,
    val displayNameLower: String,
    val city: String? = null,
)

/** First-write shape of a message (createdAt is server-resolved; MessageDoc stays the read shape). */
@Serializable
internal data class MessageCreateDoc(
    val senderUid: String,
    val text: String,
    val createdAt: BaseTimestamp,
)

/** Merge-write shape refreshing the inbox preview when a message is sent. */
@Serializable
internal data class ConversationPreviewDoc(
    val lastMessage: String,
    val lastMessageAt: BaseTimestamp,
)

/** Wire shape of one `messages/{id}` doc. */
@Serializable
internal data class MessageDoc(
    val senderUid: String = "",
    val text: String = "",
    val createdAt: Timestamp? = null,
) {
    fun toModel(id: String, currentUid: String?): Message = Message(
        id = id,
        senderUid = senderUid,
        text = text,
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
        isMine = currentUid != null && senderUid == currentUid,
    )
}

/** Wire shape of `channels/{businessId}` (business-run community channel). */
@Serializable
internal data class ChannelDoc(
    val businessId: String = "",
    val name: String = "",
    val ownerUid: String = "",
    val lastPostText: String? = null,
    val lastPostAt: Timestamp? = null,
) {
    fun toModel(id: String): diana.soleil.design.iraniancanadienbazzar.core.model.Channel =
        diana.soleil.design.iraniancanadienbazzar.core.model.Channel(
            businessId = businessId.ifBlank { id },
            name = name,
            ownerUid = ownerUid,
            lastPostText = lastPostText,
            lastPostAtMillis = lastPostAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
        )
}

/** First-write shape of a channel (createdAt server-resolved). */
@Serializable
internal data class ChannelCreateDoc(
    val businessId: String,
    val name: String,
    val ownerUid: String,
    val createdAt: BaseTimestamp,
)

/** Merge-write shape refreshing the channel preview when the owner posts. */
@Serializable
internal data class ChannelPreviewDoc(
    val lastPostText: String,
    val lastPostAt: BaseTimestamp,
)

/** Wire shape of one `channels/{businessId}/posts/{id}` broadcast. */
@Serializable
internal data class ChannelPostDoc(
    val text: String = "",
    val createdAt: Timestamp? = null,
) {
    fun toModel(id: String): diana.soleil.design.iraniancanadienbazzar.core.model.ChannelPost =
        diana.soleil.design.iraniancanadienbazzar.core.model.ChannelPost(
            id = id,
            text = text,
            createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
        )
}

/** First-write shape of a channel post (createdAt server-resolved). */
@Serializable
internal data class ChannelPostCreateDoc(
    val authorUid: String,
    val text: String,
    val createdAt: BaseTimestamp,
)

/** Server-created `users/{uid}/notifications/{id}` shape. */
@Serializable
internal data class NotificationDoc(
    val type: String = "",
    val conversationId: String? = null,
    val businessId: String? = null,
    val reviewId: String? = null,
    val postId: String? = null,
    val orderId: String? = null,
    val eventId: String? = null,
    val orderStatus: String? = null,
    val gemKind: String? = null,
    val gemId: String? = null,
    val title: String? = null,
    val preview: String? = null,
    val photoUrl: String? = null,
    val link: String? = null,
    val read: Boolean = false,
    val createdAt: Timestamp? = null,
) {
    fun toModel(id: String): AppNotification = AppNotification(
        id = id,
        type = when (type) {
            "message" -> NotificationType.Message
            "review_reply" -> NotificationType.ReviewReply
            "comment" -> NotificationType.Comment
            "order" -> NotificationType.Order
            "event" -> NotificationType.Event
            "campaign" -> NotificationType.Campaign
            "channel" -> NotificationType.Channel
            "gem" -> NotificationType.Gem
            else -> NotificationType.Other
        },
        conversationId = conversationId,
        businessId = businessId,
        reviewId = reviewId,
        postId = postId,
        orderId = orderId,
        eventId = eventId,
        orderStatus = orderStatus,
        gemKind = gemKind?.takeIf { it.isNotBlank() },
        gemId = gemId?.takeIf { it.isNotBlank() },
        title = title?.takeIf { it.isNotBlank() },
        preview = preview?.takeIf { it.isNotBlank() },
        photoUrl = photoUrl?.takeIf { it.isNotBlank() },
        link = link?.takeIf { it.isNotBlank() },
        read = read,
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
    )
}

/** Client-owned FCM registration at `users/{uid}/devices/{token}`. */
@Serializable
internal data class DeviceRegistrationDoc(
    val token: String,
    val updatedAt: BaseTimestamp,
    /** App language on this installation (`en`/`fr`/`fa`) — pushes are localized per device. */
    val language: String? = null,
)

/** First-write shape of a report (rules force reporterUid == uid). */
@Serializable
internal data class ReportCreateDoc(
    val targetType: String,
    val targetId: String,
    val reporterUid: String,
    val reason: String,
    val status: String,
    val createdAt: BaseTimestamp,
)

/** Read shape for `reports/{id}` (admin moderation queue). */
@Serializable
internal data class ReportDoc(
    val targetType: String = "",
    val targetId: String = "",
    val reporterUid: String = "",
    val reason: String = "",
    val status: String = "open",
    val createdAt: String? = null,
) {
    fun toModel(id: String): Report = Report(
        id = id,
        targetType = targetType,
        targetId = targetId,
        reporterUid = reporterUid,
        reason = reason,
        status = if (status == "resolved") ReportStatus.Resolved else ReportStatus.Open,
        createdAt = createdAt,
    )
}

/** First-write shape of a no-login listing request (`listingRequests/{id}`). All fields non-null so
 * they always reach the wire (rules require `type` in the enum, `status == 'open'`, and if present
 * `contactEmail` is a string) regardless of the encoder's encodeDefaults. */
@Serializable
internal data class ListingRequestCreateDoc(
    val businessId: String,
    val businessName: String,
    val type: String,
    val message: String,
    val contactEmail: String,
    val status: String,
    val createdAt: BaseTimestamp,
)

/** Read shape for `listingRequests/{id}` (admin moderation queue). */
@Serializable
internal data class ListingRequestDoc(
    val businessId: String = "",
    val businessName: String = "",
    val type: String = "correction",
    val message: String = "",
    val contactEmail: String = "",
    val status: String = "open",
    val createdAt: Timestamp? = null,
) {
    fun toModel(id: String): ListingRequest = ListingRequest(
        id = id,
        businessId = businessId,
        businessName = businessName,
        type = when (type) {
            "removal" -> ListingRequestType.Removal
            "privacy" -> ListingRequestType.Privacy
            else -> ListingRequestType.Correction
        },
        message = message,
        contactEmail = contactEmail.ifBlank { null },
        status = if (status == "resolved") ListingRequestStatus.Resolved else ListingRequestStatus.Open,
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
    )
}

/** First-write shape of a moderation audit entry (`moderationActions/{id}`). Fields match the
 * rules' `hasOnly` allow-list exactly; `createdAt` is a server timestamp. */
@Serializable
internal data class ModerationActionCreateDoc(
    val actorUid: String,
    val action: String,
    val targetType: String,
    val targetId: String,
    val reason: String? = null,
    val createdAt: BaseTimestamp,
)

/** Read shape for `moderationActions/{id}` (admin + moderator audit trail, newest-first). */
@Serializable
internal data class ModerationActionDoc(
    val actorUid: String = "",
    val action: String = "",
    val targetType: String = "",
    val targetId: String = "",
    val reason: String? = null,
    val createdAt: Timestamp? = null,
) {
    fun toModel(id: String): ModerationAction = ModerationAction(
        id = id,
        actorUid = actorUid,
        action = action,
        targetType = targetType,
        targetId = targetId,
        reason = reason,
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
    )
}

/** First-write shape of a public "I'm going" mark (`events/{id}/rsvps/{uid}`). */
@Serializable
internal data class RsvpCreateDoc(val uid: String, val eventId: String, val createdAt: BaseTimestamp)

/** First-write shape of the private RSVP mirror (`users/{uid}/rsvps/{eventId}`). */
@Serializable
internal data class RsvpMirrorDoc(val goingAt: BaseTimestamp)

/** Read shape for `deals/{id}` (merchant promotions). */
@Serializable
internal data class DealDoc(
    val businessId: String = "",
    val businessName: String = "",
    val title: LocalizedTextDoc = LocalizedTextDoc(),
    val details: LocalizedTextDoc? = null,
    val endsAt: Timestamp? = null,
    val visible: Boolean = true,
    val createdAt: Timestamp? = null,
) {
    fun toModel(id: String): diana.soleil.design.iraniancanadienbazzar.core.model.Deal =
        diana.soleil.design.iraniancanadienbazzar.core.model.Deal(
            id = id,
            businessId = businessId,
            businessName = businessName,
            title = title.toModel(),
            details = details?.toModel(),
            endsAtMillis = endsAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 } ?: 0L,
            visible = visible,
            createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
        )
}

/** First-write shape of a deal (owner dashboard authoring); matches the rules' hasOnly allow-list. */
@Serializable
internal data class DealCreateDoc(
    val businessId: String,
    val businessName: String,
    val title: LocalizedTextDoc,
    val details: LocalizedTextDoc?,
    val endsAt: Timestamp,
    val visible: Boolean,
    val createdAt: BaseTimestamp,
)

/** Read shape for `businesses/{id}/metrics/counters` — written ONLY by the recordListingMetric Function. */
@Serializable
internal data class ListingCountersDoc(
    val views: Long = 0,
    val calls: Long = 0,
    val directions: Long = 0,
    val chats: Long = 0,
    val shares: Long = 0,
) {
    fun toModel(): ListingCounters = ListingCounters(
        views = views,
        calls = calls,
        directions = directions,
        chats = chats,
        shares = shares,
    )
}

/** Read shape for `businesses/{id}/metrics/daily-YYYYMMDD` buckets (recordListingMetric-written). */
@Serializable
internal data class DailyCountersDoc(
    val day: String = "",
    val views: Long = 0,
    val calls: Long = 0,
    val directions: Long = 0,
    val chats: Long = 0,
    val shares: Long = 0,
) {
    fun toModel(): DailyListingCounters = DailyListingCounters(
        day = day,
        counters = ListingCounters(
            views = views,
            calls = calls,
            directions = directions,
            chats = chats,
            shares = shares,
        ),
    )
}

/** Tolerant read shape for `users/{uid}` role (admin gating; other doc fields ignored). */
@Serializable
internal data class UserRoleDoc(val role: String? = null)

/** Daily platform growth bucket (`platform_metrics/daily-*`) for the admin growth chart. */
@Serializable
internal data class PlatformDailyDoc(
    val day: String = "",
    val users: Int = 0,
    val businesses: Int = 0,
) {
    fun toModel(): DailyPlatformCounters = DailyPlatformCounters(day, users, businesses)
}

/** Tolerant read shape for the admin console's `users/{uid}` docs list; every field optional. */
@Serializable
internal data class UserAccountDoc(
    val displayName: String? = null,
    val role: String? = null,
    val metro: String? = null,
    val preferredLanguage: String? = null,
) {
    fun toModel(uid: String): UserAccount = UserAccount(
        uid = uid,
        displayName = displayName?.takeIf { it.isNotBlank() },
        role = role ?: UserAccount.ROLE_USER,
        metroId = metro,
        preferredLanguage = preferredLanguage,
    )
}

/** Tolerant read shape for `users/{uid}` preferences: all fields optional, other doc fields ignored. */
@Serializable
internal data class UserPrefsReadDoc(
    val preferredLanguage: String? = null,
    val metro: String? = null,
    val savedBusinessIds: List<String>? = null,
    val savedNewsIds: List<String>? = null,
    val interests: List<String>? = null,
) {
    fun toModel(): UserPreferences = UserPreferences(
        preferredLanguage = preferredLanguage,
        metroId = metro,
        savedBusinessIds = savedBusinessIds,
        savedNewsIds = savedNewsIds,
        interests = interests,
    )
}

/** Tolerant read shape for the user's editable profile fields on `users/{uid}`. */
@Serializable
internal data class UserProfileReadDoc(
    val displayName: String? = null,
    val bio: String? = null,
    val createdAt: Timestamp? = null,
    val pushEnabled: Boolean = true,
    val gemPushEnabled: Boolean = true,
    val emailMarketing: EmailMarketingReadDoc? = null,
    val pro: ProReadDoc? = null,
) {
    fun toModel(): UserProfile = UserProfile(
        displayName = displayName,
        bio = bio,
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
        pushEnabled = pushEnabled,
        gemPushEnabled = gemPushEnabled,
        emailMarketingConsented = emailMarketing?.consented == true,
        proActive = pro?.active == true,
        proPlan = pro?.plan,
        proUntilMillis = pro?.currentPeriodEnd?.let { runCatching { Instant.parse(it).toEpochMilliseconds() }.getOrNull() },
    )
}

/** Read shape for the Mahaleh Pro entitlement (`users/{uid}.pro`), written by the Stripe webhook. */
@Serializable
internal data class ProReadDoc(
    val active: Boolean = false,
    val plan: String? = null,
    val status: String? = null,
    val currentPeriodEnd: String? = null,
    val stripeCustomerId: String? = null,
    val stripeSubscriptionId: String? = null,
)

/** Tolerant read shape for the CASL email-consent map on `users/{uid}.emailMarketing`. */
@Serializable
internal data class EmailMarketingReadDoc(
    val consented: Boolean = false,
)

/** Merge-write shape for editing the user's name + bio (role/preferences untouched). */
@Serializable
internal data class UserProfileUpdateDoc(
    val displayName: String,
    val bio: String,
)

/** Merge-write shape for the push-notification opt-in only (`users/{uid}.pushEnabled`). */
@Serializable
internal data class UserPushPrefDoc(
    val pushEnabled: Boolean,
)

/** Merge-write shape for the daily-gem opt-in only (`users/{uid}.gemPushEnabled`). */
@Serializable
internal data class UserGemPushPrefDoc(
    val gemPushEnabled: Boolean,
)

/**
 * Merge-write shape for the CASL email-marketing consent (`users/{uid}.emailMarketing`). The
 * server timestamp + source are the CASL proof-of-consent record; `sendCampaign` (email channel)
 * only targets users whose `consented` is true.
 */
@Serializable
internal data class UserEmailPrefDoc(
    val emailMarketing: EmailMarketingWriteDoc,
)

@Serializable
internal data class EmailMarketingWriteDoc(
    val consented: Boolean,
    val updatedAt: BaseTimestamp,
    val source: String,
)

@Serializable
internal data class CategoryDoc(
    val name: String = "",
    val iconKey: String = "category",
    val businessCount: Int = 0,
    val order: Int = 0,
    val nameLocalized: LocalizedTextDoc? = null,
    val businessCountByMetro: Map<String, Int> = emptyMap(),
    val businessCountByCountry: Map<String, Int> = emptyMap(),
) {
    fun toModel(id: String): Category = Category(
        id = id,
        name = name,
        iconKey = iconKey,
        businessCount = businessCount,
        order = order,
        nameLocalized = nameLocalized?.toModel(),
        businessCountByMetro = businessCountByMetro,
        businessCountByCountry = businessCountByCountry,
    )
}

@Serializable
internal data class NewsDoc(
    val title: LocalizedTextDoc = LocalizedTextDoc(),
    val summary: LocalizedTextDoc = LocalizedTextDoc(),
    val body: LocalizedTextDoc = LocalizedTextDoc(),
    val imageUrl: String? = null,
    val linkUrl: String? = null,
    val metroId: String? = null,
    val visible: Boolean = false,
    val publishedAt: Timestamp? = null,
) {
    fun toModel(id: String): NewsArticle? {
        val published = publishedAt ?: return null
        return NewsArticle(
            id = id,
            title = title.toModel(),
            summary = summary.toModel(),
            body = body.toModel(),
            imageUrl = imageUrl,
            linkUrl = linkUrl,
            metroId = metroId,
            publishedAtMillis = published.seconds * 1_000 + published.nanoseconds / 1_000_000,
        )
    }
}

/** First-write shape of `listingChangeRequests/{id}` (rules pin requestedBy, status and ownership). */
@Serializable
internal data class ListingChangeRequestCreateDoc(
    val businessId: String,
    val businessName: String,
    val requestedBy: String,
    val proposedName: String?,
    val proposedCategoryId: String?,
    val proposedAddress: AddressDoc?,
    val currentName: String,
    val currentCategoryId: String,
    val currentAddress: AddressDoc,
    val status: String,
    val createdAt: BaseTimestamp,
)

/** Read shape for `listingChangeRequests/{id}` (owner banner + admin queue). */
@Serializable
internal data class ListingChangeRequestDoc(
    val businessId: String = "",
    val businessName: String = "",
    val requestedBy: String = "",
    val proposedName: String? = null,
    val proposedCategoryId: String? = null,
    val proposedAddress: AddressDoc? = null,
    val currentName: String = "",
    val currentCategoryId: String = "",
    val currentAddress: AddressDoc? = null,
    val status: String = "pending",
    val rejectionReason: String? = null,
    val createdAt: Timestamp? = null,
) {
    fun toModel(id: String) = diana.soleil.design.iraniancanadienbazzar.core.model.ListingChangeRequest(
        id = id,
        businessId = businessId,
        businessName = businessName,
        requestedBy = requestedBy,
        proposedName = proposedName,
        proposedCategoryId = proposedCategoryId,
        proposedAddress = proposedAddress?.toModel(),
        currentName = currentName,
        currentCategoryId = currentCategoryId,
        currentAddress = currentAddress?.toModel(),
        status = when (status) {
            "approved" -> diana.soleil.design.iraniancanadienbazzar.core.model.ChangeRequestStatus.Approved
            "rejected" -> diana.soleil.design.iraniancanadienbazzar.core.model.ChangeRequestStatus.Rejected
            else -> diana.soleil.design.iraniancanadienbazzar.core.model.ChangeRequestStatus.Pending
        },
        rejectionReason = rejectionReason,
        createdAtMillis = createdAt?.let { it.seconds * 1_000 + it.nanoseconds / 1_000_000 },
    )
}
